import type { UseQueryResult } from '@tanstack/react-query';

import Enums from '@core/types/api/Enums';
import type { User } from '@core/types/api/User/User';
import type { List } from '@core/types/list';

import { useSlabQuery } from './useSlabQuery';

/**
 * Drops users that must never appear in a picker — currently the internal
 * "Slabstack Admin" account, which the server-side salesperson filters do not
 * exclude. Declared at module scope so react-query can memoize the selection
 * instead of re-running it on every render.
 */
export const selectSelectableSalespeople = (data: List<User>): List<User> => {
  const items = data.items.filter((user) => user.isSelectable());
  return { items, count: items.length };
};

/**
 * Shared hook for fetching the active salesperson list.
 * Replaces 5+ identical `useSlabQuery('GET users', ...)` calls that all
 * filter to `isSalesperson=true` and `isDeactivated=false`, and applies the
 * same `isSelectable()` rule every other option list in the app uses.
 */
export const useSalespersonList = (): UseQueryResult<List<User>> =>
  useSlabQuery(
    'GET users',
    {
      queryParams: {
        filterBy: [
          {
            operation: Enums.FilterOperation.Equals,
            name: 'isSalesperson',
            values: [true],
          },
          {
            name: 'isDeactivated',
            operation: Enums.FilterOperation.Equals,
            values: [false],
          },
        ],
        perPage: 0,
        page: 0,
      },
    },
    { select: selectSelectableSalespeople },
  );
